/**
 * Alta de equipo.
 *
 * Sólo alta: **un equipo no se edita, se mueve o se da de baja**. Cambiar
 * su ubicación a mano borraría el histórico de traslados, que es la
 * trazabilidad que el §18 del prompt maestro exige mantener.
 *
 * Las fechas de ciclo de vida son lo delicado. El timbrado de un extintor
 * no es una «fecha de caducidad»: es el punto de partida de un contador de
 * tres retimbrados (filas B-12 y B-13 de la matriz). Ponerlo mal no da un
 * error: da un plan de mantenimiento equivocado durante veinte años.
 */

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSession } from '../../session/session-context.js';
import { ApiError } from '../../session/api-error.js';
import { useForm } from '../../forms/use-form.js';
import { FormSection, SelectField, TextArea, TextField } from '../../forms/fields.js';
import { EntityPicker } from '../../forms/entity-picker.js';
import { isoDate, maxLength, notInFuture, required, type FieldRules } from '../../forms/form-state.js';

interface EquipmentType {
  code: string;
  name: string;
  requires_serial: boolean;
  lifecycle_kind: string;
}

const TODAY = new Date().toISOString().slice(0, 10);

const RULES: FieldRules = {
  siteId: [required('El centro')],
  equipmentTypeCode: [required('El tipo de equipo')],
  serialNumber: [maxLength(64)],
  installationDate: [isoDate(), notInFuture(TODAY)],
  commissioningDate: [isoDate(), notInFuture(TODAY)],
  stampingDate: [isoDate(), notInFuture(TODAY)],
  restamping1On: [isoDate(), notInFuture(TODAY)],
  restamping2On: [isoDate(), notInFuture(TODAY)],
  restamping3On: [isoDate(), notInFuture(TODAY)],
};

const NULLABLE = [
  'serialNumber',
  'lot',
  'installationDate',
  'commissioningDate',
  'manufacturingDate',
  'stampingDate',
  'restamping1On',
  'restamping2On',
  'restamping3On',
  'brand',
  'extinguisherKind',
  'capacity',
  'capacityUnit',
  'locationId',
];

export function EquipmentFormScreen(): React.ReactNode {
  const { api } = useSession();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const [types, setTypes] = useState<EquipmentType[]>([]);
  const [created, setCreated] = useState<{ internalCode: string } | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    api
      .get<{ items: EquipmentType[] }>('/regulation/equipment-types')
      .then((data) => {
        if (!cancelled) setTypes(data.items ?? []);
      })
      .catch((caught: unknown) => {
        // Sin catálogo el formulario no puede funcionar: se dice, en lugar
        // de dejar un desplegable vacío que parece un fallo del usuario.
        if (!cancelled) {
          setLoadError(
            caught instanceof ApiError
              ? `No se ha podido cargar el catálogo de tipos: ${caught.message}`
              : 'No se ha podido cargar el catálogo de tipos de equipo.',
          );
        }
      });

    return () => {
      cancelled = true;
    };
  }, [api]);

  const form = useForm<{ id: string; internalCode: string }>({
    mode: 'create',
    rules: RULES,
    nullable: NULLABLE,
    initial: {
      siteId: params.get('siteId') ?? '',
      equipmentTypeCode: '',
      internalCode: '',
      serialNumber: '',
      lot: '',
      manufacturingDate: '',
      installationDate: '',
      commissioningDate: '',
      stampingDate: '',
      restamping1On: '',
      restamping2On: '',
      restamping3On: '',
      brand: '',
      extinguisherKind: '',
      capacity: '',
      capacityUnit: '',
      capacidadElegida: '',
      restampingCount: 0,
      notes: '',
    },
    onSubmit: async (body, client) => {
      const payload: Record<string, unknown> = { ...body };
      if (payload['restampingCount'] !== undefined && payload['restampingCount'] !== '') {
        payload['restampingCount'] = Number(payload['restampingCount']);
      }
      return client.post<{ id: string; internalCode: string }>('/equipment', payload);
    },
    onSaved: (saved) => setCreated(saved),
  });

  const selectedType = types.find((t) => t.code === form.values['equipmentTypeCode']);
  const isExtinguisher = selectedType?.lifecycle_kind === 'EXTINGUISHER';

  return (
    <section className="screen">
      <header className="screen__head">
        <h1 className="screen__title">Nuevo equipo</h1>
        {form.dirty && <span className="screen__count">Cambios sin guardar</span>}
      </header>

      {loadError !== null && (
        <p className="screen__error" role="alert">
          {loadError}
        </p>
      )}

      {created && (
        <p className="screen__note screen__note--strong">
          Equipo <strong>{created.internalCode}</strong> creado. Su plan de mantenimiento ya está
          calculado a partir del reglamento vigente.{' '}
          <button className="table__link" type="button" onClick={() => { setCreated(null); form.reset(); }}>
            Dar de alta otro
          </button>
        </p>
      )}

      {form.formError !== null && (
        <p className="screen__error" role="alert">
          {form.formError}
        </p>
      )}

      {form.orphanErrors.length > 0 && (
        <p className="screen__error" role="alert">
          El servidor ha rechazado campos que este formulario no muestra:{' '}
          {form.orphanErrors.join(' · ')}
        </p>
      )}

      <form className="form" onSubmit={(e) => void form.submit(e)} noValidate>
        <FormSection title="Ubicación e identidad">
          <EntityPicker
            label="Centro"
            entity="SITE"
            value={String(form.values['siteId'] ?? '')}
            onChange={(v) => form.set('siteId', v)}
            error={form.errors['siteId']}
            required
            help="Se busca por nombre o código. Sólo aparecen los centros de su cartera."
          />
          <SelectField
            label="Tipo de equipo"
            value={String(form.values['equipmentTypeCode'] ?? '')}
            onChange={(v) => form.set('equipmentTypeCode', v)}
            onBlur={() => form.blur('equipmentTypeCode')}
            error={form.errors['equipmentTypeCode']}
            options={types.map((t) => ({ value: t.code, label: `${t.code} · ${t.name}` }))}
            emptyLabel="— Elija un tipo —"
            required
            help="Determina qué reglas de mantenimiento se le aplican."
          />
          <TextField
            label="Código interno"
            value={String(form.values['internalCode'] ?? '')}
            onChange={(v) => form.set('internalCode', v)}
            maxLength={32}
            help="Si se deja vacío, el sistema lo genera siguiendo la serie del tipo."
          />
          <TextField
            label="Número de serie"
            value={String(form.values['serialNumber'] ?? '')}
            onChange={(v) => form.set('serialNumber', v)}
            onBlur={() => form.blur('serialNumber')}
            error={form.errors['serialNumber']}
            required={selectedType?.requires_serial ?? false}
            maxLength={64}
            help={
              selectedType?.requires_serial
                ? 'Este tipo lo exige: es la identificación unívoca que pide el anexo II §5.b.'
                : undefined
            }
          />
          <TextField
            label="Lote"
            value={String(form.values['lot'] ?? '')}
            onChange={(v) => form.set('lot', v)}
            maxLength={64}
          />
        </FormSection>

        <FormSection
          title="Ciclo de vida"
          description={
            <>
              De estas fechas sale el plan de mantenimiento del equipo para los próximos veinte años.
              Un dato mal puesto no da error: da un plan equivocado.
            </>
          }
        >
          <TextField
            label="Fecha de fabricación"
            type="date"
            value={String(form.values['manufacturingDate'] ?? '')}
            onChange={(v) => form.set('manufacturingDate', v)}
          />
          <TextField
            label="Fecha de instalación"
            type="date"
            value={String(form.values['installationDate'] ?? '')}
            onChange={(v) => form.set('installationDate', v)}
            onBlur={() => form.blur('installationDate')}
            error={form.errors['installationDate']}
          />
          <TextField
            label="Puesta en servicio"
            type="date"
            value={String(form.values['commissioningDate'] ?? '')}
            onChange={(v) => form.set('commissioningDate', v)}
            onBlur={() => form.blur('commissioningDate')}
            error={form.errors['commissioningDate']}
            help="Es la fecha desde la que se cuentan los plazos si no hay mantenimiento previo."
          />

          {isExtinguisher && (
            <>
              <TextField
                label="Fecha de timbrado"
                type="date"
                value={String(form.values['stampingDate'] ?? '')}
                onChange={(v) => form.set('stampingDate', v)}
                onBlur={() => form.blur('stampingDate')}
                error={form.errors['stampingDate']}
                help="Prueba de nivel C. NO es una fecha de caducidad: arranca un contador de tres retimbrados (fila B-12)."
              />
              {/* Antes esto era un contador. Un número no dice CUÁNDO se hizo
                  cada retimbrado, que es lo que pregunta una inspección y lo
                  que hay que poder enseñar. Ahora son las fechas, y el
                  contador se calcula solo. */}
              <TextField
                label="Retimbrado 1"
                type="date"
                value={String(form.values['restamping1On'] ?? '')}
                onChange={(v) => form.set('restamping1On', v)}
                onBlur={() => form.blur('restamping1On')}
                error={form.errors['restamping1On']}
                help="A los cinco años del timbrado inicial."
              />
              <TextField
                label="Retimbrado 2"
                type="date"
                value={String(form.values['restamping2On'] ?? '')}
                onChange={(v) => form.set('restamping2On', v)}
                onBlur={() => form.blur('restamping2On')}
                error={form.errors['restamping2On']}
              />
              <TextField
                label="Retimbrado 3"
                type="date"
                value={String(form.values['restamping3On'] ?? '')}
                onChange={(v) => form.set('restamping3On', v)}
                onBlur={() => form.blur('restamping3On')}
                error={form.errors['restamping3On']}
                help="El tercero es el último: después el extintor no vuelve al servicio, procede sustitución (fila B-13)."
              />

              <TextField
                label="Marca"
                value={String(form.values['brand'] ?? '')}
                onChange={(v) => form.set('brand', v)}
                maxLength={120}
              />
              <SelectField
                label="Tipo de agente"
                value={String(form.values['extinguisherKind'] ?? '')}
                onChange={(v) => form.set('extinguisherKind', v)}
                options={[
                  { value: '', label: 'Sin especificar' },
                  { value: 'ABC', label: 'Polvo ABC' },
                  { value: 'BC', label: 'Polvo BC' },
                  { value: 'CO2', label: 'CO₂' },
                  { value: 'HIDRICO', label: 'Hídrico' },
                  { value: 'ESPUMA', label: 'Espuma' },
                  { value: 'CLASE_D', label: 'Clase D — metales' },
                  { value: 'CLASE_F', label: 'Clase F — cocinas' },
                ]}
                help="Decide para qué fuego sirve. Se elige de la lista para que no acabe escrito de cinco formas distintas."
              />
              <SelectField
                label="Capacidad"
                value={String(form.values['capacidadElegida'] ?? '')}
                onChange={(v) => {
                  form.set('capacidadElegida', v);
                  // El desplegable lleva número y unidad juntos porque «6» no
                  // significa lo mismo en kilos que en litros, y separarlos en
                  // dos campos invita a dejarse la unidad sin poner.
                  const [numero, unidad] = v.split(' ');
                  form.set('capacity', numero ?? '');
                  form.set('capacityUnit', unidad ?? '');
                }}
                options={[
                  { value: '', label: 'Sin especificar' },
                  { value: '1 KG', label: '1 kg' },
                  { value: '2 KG', label: '2 kg' },
                  { value: '3 KG', label: '3 kg' },
                  { value: '5 KG', label: '5 kg' },
                  { value: '6 KG', label: '6 kg' },
                  { value: '9 KG', label: '9 kg' },
                  { value: '25 KG', label: '25 kg' },
                  { value: '50 KG', label: '50 kg' },
                  { value: '6 L', label: '6 litros' },
                  { value: '9 L', label: '9 litros' },
                ]}
              />
            </>
          )}

          <TextArea
            label="Notas"
            value={String(form.values['notes'] ?? '')}
            onChange={(v) => form.set('notes', v)}
          />
        </FormSection>

        <footer className="form__actions">
          <button className="button button--primary" type="submit" disabled={form.saving}>
            {form.saving ? 'Creando…' : 'Crear equipo'}
          </button>
          <button
            className="button button--quiet"
            type="button"
            onClick={() => {
              if (form.dirty && !window.confirm('Hay cambios sin guardar. ¿Salir igualmente?')) return;
              void navigate('/equipos');
            }}
          >
            Volver
          </button>
        </footer>
      </form>
    </section>
  );
}
